import { useState } from "react";
import { Link } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { Lock, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { CoreVideo } from "./universe/CoreVideo";
import { RippMark } from "./RippMark";

export const Login = ({ onAuthed }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await api.login(email.trim(), password);
      setAuthToken(res.token);
      onAuthed(res.user);
    } catch (e) {
      setErr("Invalid email or password");
      setBusy(false);
    }
  };

  return (
    <div className="ripp-noise relative min-h-screen bg-[#050A14] font-body text-white flex items-center justify-center px-4">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="login-video-layer">
        <CoreVideo className="h-full w-full object-cover opacity-35 login-video-in" testid="login-video" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,10,20,0.45)_0%,rgba(5,10,20,0.92)_70%)]" />
      </div>
      <div className="ripp-radial pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="ripp-dotgrid pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-sm">
        <Link to="/" data-testid="login-back-home"
          className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/40 transition-colors hover:text-[#38BDF8]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to ripp.xyz
        </Link>

        <div className="mb-6 flex items-center gap-2">
          <RippMark size={34} className="text-[#38BDF8]" glow testid="login-mark" />
          <span className="font-display text-xl font-extrabold tracking-tight text-white">RIPP<span className="text-[#38BDF8]"> LABS</span></span>
        </div>

        <form onSubmit={submit} data-testid="login-form"
          className="rounded-lg border border-white/10 bg-[#0B1220]/80 p-8 backdrop-blur-xl">
          <div className="mb-1 flex items-center gap-2">
            <Lock size={14} className="text-[#38BDF8]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Secure Access</span>
          </div>
          <h1 className="mb-7 font-display text-2xl font-extrabold tracking-tighter text-white">Enter the Terminal</h1>

          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-white/40">Email</label>
          <input
            data-testid="login-email"
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required
            className="mb-4 w-full rounded-sm border border-white/15 bg-[#050A14] px-3 py-2.5 font-mono text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#38BDF8]/60"
            placeholder="you@ripp.xyz"
          />
          <label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-white/40">Password</label>
          <input
            data-testid="login-password"
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="mb-5 w-full rounded-sm border border-white/15 bg-[#050A14] px-3 py-2.5 font-mono text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#38BDF8]/60"
            placeholder="••••••••"
          />
          {err && <div data-testid="login-error" className="mb-4 font-mono text-xs text-red-400">{err}</div>}
          <button
            data-testid="login-submit" type="submit" disabled={busy}
            className="group flex w-full items-center justify-center gap-2 rounded-sm bg-[#38BDF8] py-3 font-mono text-sm font-semibold uppercase tracking-wider text-[#041018] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(56,189,248,0.4)] disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            {busy ? "Signing in…" : "Sign In"}
            {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
          </button>
        </form>

        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-white/25">
          Build your strategy — let it RIPP
        </p>
      </div>
    </div>
  );
};
